import { useState } from "react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { api, formatApiError } from "@/lib/api";
import { usePageTitle } from "@/lib/content";
import { Reveal } from "@/components/site/Reveal";

export default function Contact() {
  usePageTitle("Contact — Sitebound", "Get in touch with the Sitebound team about your order or our construction job seeker packs.");
  const [form, setForm] = useState({ name: "", email: "", message: "", website: "" });
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/contact", form);
      toast.success("Message sent", { description: "We'll get back to you as soon as we can." });
      setForm({ name: "", email: "", message: "", website: "" });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  return (
    <div data-testid="contact-page" className="mx-auto max-w-3xl px-4 py-16 sm:px-6 md:py-24 lg:px-8">
      <Reveal>
        <div className="mono-label">Contact</div>
        <h1 className="font-display mt-4 text-5xl font-extrabold uppercase tracking-tight text-slate-100 sm:text-6xl">
          Talk to us
        </h1>
        <p className="mt-5 text-base leading-relaxed text-slate-400">
          Questions about an order, a download link, or which pack suits you? Send it through.
        </p>
        <p className="mt-4 text-sm text-slate-400">
          Prefer email?{" "}
          <a
            data-testid="contact-email-link"
            href="mailto:hello@sitebound.com.au"
            className="text-[#FF5500] underline underline-offset-2"
          >
            hello@sitebound.com.au
          </a>
        </p>
      </Reveal>
      <Reveal delay={0.1}>
        <form onSubmit={submit} className="mt-12 space-y-5">
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
            <div>
              <label className="mono-label mb-2 block" htmlFor="contact-name">Name</label>
              <Input
                id="contact-name"
                data-testid="contact-name-input"
                required
                value={form.name}
                onChange={set("name")}
                className="border-white/15 bg-[#121418] text-slate-100"
              />
            </div>
            <div>
              <label className="mono-label mb-2 block" htmlFor="contact-email">Email</label>
              <Input
                id="contact-email"
                data-testid="contact-email-input"
                type="email"
                required
                value={form.email}
                onChange={set("email")}
                className="border-white/15 bg-[#121418] text-slate-100"
              />
            </div>
          </div>
          <div>
            <label className="mono-label mb-2 block" htmlFor="contact-message">Message</label>            <Textarea
              id="contact-message"
              data-testid="contact-message-input"
              required
              rows={6}
              value={form.message}
              onChange={set("message")}
              className="border-white/15 bg-[#121418] text-slate-100"
            />
          </div>
          {/* honeypot — hidden from real users, catches bots */}
          <input
            type="text"
            name="website"
            value={form.website}
            onChange={set("website")}
            className="hidden"
            tabIndex={-1}
            autoComplete="off"
            aria-hidden="true"
          />
          <button
            data-testid="contact-submit-button"
            type="submit"
            disabled={loading}
            className="bg-[#FF5500] px-8 py-3.5 text-sm font-bold uppercase tracking-widest text-white transition-colors hover:bg-[#E04800] disabled:opacity-60"
          >
            {loading ? "Sending…" : "Send message"}
          </button>
        </form>
      </Reveal>
    </div>
  );
}
